import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { UPGRADE } from 'constants/mixpanel';
import { SaveAsTeamTemplateRequest, saveAsTeamTemplate as saveAsTeamTemplateAPI } from 'libs/e1api/teamtemplate';
import { SaveAsMyTemplateRequest, saveAsMyTemplate as saveAsMyTemplateAPI } from 'libs/e1api/template';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { useE2Client } from 'modules/E2ClientProvider';
import { RootState, store } from 'modules/Editor/store';
import { selectInfographTitle } from 'modules/Editor/store/infographSelector';
import { useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { useUser } from '../useUser';

export const useSaveTemplate = () => {
  const toast = useToast();
  const { client, infographId: infographIdFromUseE2Client } = useE2Client();
  const { t } = useTranslation('common_navbar');
  const { isFreeUser, isPremiumUser, userType, userId } = useUser();
  const isBusinessGated = isFreeUser || isPremiumUser;

  const saveAsMyTemplate = useCallback(
    async ({
      saveTemplateRequest,
      loadingToastString,
      successToastString,
      errorToastString,
    }: {
      saveTemplateRequest: SaveAsMyTemplateRequest;
      loadingToastString: string;
      successToastString: string;
      errorToastString: string;
    }) => {
      try {
        if (!client || !infographIdFromUseE2Client) return;

        const saveTemplateToastId = 'save-as-my-template-toast';
        toast({
          id: saveTemplateToastId,
          title: loadingToastString,
          status: 'info',
        });

        const saveTemplateResult = await saveAsMyTemplateAPI(saveTemplateRequest);
        toast.close(saveTemplateToastId);

        if (!saveTemplateResult) {
          throw new Error('Failed to save as my template');
        }

        const { id, status } = saveTemplateResult;
        if (!id) {
          console.log({ status });
          throw new Error('Failed to save as my template with status: ' + status);
        }

        toast({ title: successToastString });
        return id;
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: errorToastString,
        });
      }
      return null;
    },
    [client, infographIdFromUseE2Client, toast],
  );

  const saveAsTeamTemplate = useCallback(
    async ({
      saveTemplateRequest,
      loadingToastString,
      successToastString,
      errorToastString,
    }: {
      saveTemplateRequest: SaveAsTeamTemplateRequest;
      loadingToastString: string;
      successToastString: string;
      errorToastString: string;
    }) => {
      try {
        if (!client || !infographIdFromUseE2Client) return;

        const saveTemplateToastId = 'save-as-team-template-toast';
        toast({
          id: saveTemplateToastId,
          title: loadingToastString,
          status: 'info',
        });

        const saveTemplateResult = await saveAsTeamTemplateAPI(saveTemplateRequest);
        toast.close(saveTemplateToastId);

        if (!saveTemplateResult) {
          throw new Error('Failed to save as team template');
        }

        const { id, status } = saveTemplateResult;
        if (!id) {
          console.log({ status });
          throw new Error('Failed to save as team template with status: ' + status);
        }

        toast({ title: successToastString });
        return id;
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: errorToastString,
        });
      }
      return null;
    },
    [client, infographIdFromUseE2Client, toast],
  );

  const onSaveAsMyTemplate = async () => {
    try {
      if (isBusinessGated) {
        Mixpanel.track(UPGRADE.eventName, {
          Feature: UPGRADE.feature.SaveAsMyTemplate,
          'User Plan': getUserPlanProperty(userType),
        });
        setAnalyticsUpgradeFeatureProp(UPGRADE.feature.SaveAsMyTemplate, 'save as template');
        window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
        return;
      }
      const storeState = store.getState() as RootState;
      const infographTitle = selectInfographTitle(storeState);

      const urlParts = window.location.href.split('/');
      const designId = urlParts[urlParts.indexOf('design') + 1];

      const saveTemplateRequest: SaveAsMyTemplateRequest = { infographic: designId };
      const templateType = t('fileMenu.saveAsTemplate.myTemplate.templateType');

      // toast calls happen within the useSaveTemplate hook
      const id = await saveAsMyTemplate({
        saveTemplateRequest,
        loadingToastString: t('fileMenu.saveAsTemplate.loadingToast', { infographTitle, templateType }),
        successToastString: t('fileMenu.saveAsTemplate.successToast', { infographTitle, templateType }),
        errorToastString: t('fileMenu.saveAsTemplate.failToast'),
      });

      if (id) {
        // Todo: finalize mixpanel event properties

        // Mixpanel.track(SAVE_AS_TEMPLATE.eventName, {
        //   from: 'My Template',
        //   template: id,
        // });
        // window.open(`/infographics/templates`);
        console.log('save as my template successful');
      }
    } catch (error) {
      console.error(error);
      toast({
        title: t('fileMenu.saveAsTemplate.failToast'),
        status: 'error',
      });
    }
  };

  const onSaveAsTeamTemplate = async () => {
    try {
      if (!userId) {
        console.log('User id is required to save as team template.');
        return;
      }

      if (isBusinessGated) {
        Mixpanel.track(UPGRADE.eventName, {
          Feature: UPGRADE.feature.SaveAsTeamTemplate,
          'User Plan': getUserPlanProperty(userType),
        });
        setAnalyticsUpgradeFeatureProp(UPGRADE.feature.SaveAsTeamTemplate, 'save as team template');
        window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
        return;
      }
      const storeState = store.getState() as RootState;
      const infographTitle = selectInfographTitle(storeState);

      const urlParts = window.location.href.split('/');
      const designId = urlParts[urlParts.indexOf('design') + 1];

      const saveTemplateRequest: SaveAsTeamTemplateRequest = {
        infographic: designId,
        owner_id: userId,
      };

      const templateType = t('fileMenu.saveAsTemplate.teamTemplate.templateType');
      const id = await saveAsTeamTemplate({
        saveTemplateRequest,
        loadingToastString: t('fileMenu.saveAsTemplate.loadingToast', { infographTitle, templateType }),
        successToastString: t('fileMenu.saveAsTemplate.successToast', { infographTitle, templateType }),
        errorToastString: t('fileMenu.saveAsTemplate.failToast'),
      });

      if (id) {
        // Todo: finalize mixpanel event properties

        // Mixpanel.track(SAVE_AS_TEMPLATE.eventName, {
        //   from: 'My Template',
        //   template: id,
        // });
        // window.open(`/infographics/team_templates`);
        console.log('save as team template successful');
      }
    } catch (error) {
      console.error(error);
      toast({
        title: t('fileMenu.saveAsTemplate.failToast'),
        status: 'error',
      });
    }
  };

  return { onSaveAsMyTemplate, onSaveAsTeamTemplate };
};
